import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTelegramLinkStatus, generateTelegramLinkCode, unlinkTelegram } from '../api/users';
import { useTranslation } from '../i18n';
import analytics from '../lib/analytics';

export default function TelegramLinkPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ['telegram', 'link-status'],
    queryFn: () => getTelegramLinkStatus().then((r) => r.data.data),
  });

  // Linking itself completes server-side (user finishes /start in Telegram,
  // not in this browser tab) — there's no click here to hang the event on.
  // Detect the pending → linked transition instead; React Query's default
  // refetch-on-window-focus catches it when the user comes back from
  // Telegram. Ref instead of state so this doesn't trigger its own re-render.
  const prevLinked = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (status && prevLinked.current === false && status.linked === true) {
      analytics.trackTelegramLinked();
    }
    if (status) prevLinked.current = status.linked;
  }, [status]);

  const linkMutation = useMutation({
    mutationFn: () => generateTelegramLinkCode().then((r) => r.data.data),
  });

  const unlinkMutation = useMutation({
    mutationFn: () => unlinkTelegram(),
    onSuccess: () => {
      linkMutation.reset();
      analytics.trackTelegramUnlinked();
      void qc.invalidateQueries({ queryKey: ['telegram', 'link-status'] });
    },
  });

  if (!status) return null;

  return (
    <div style={card}>
      <div style={headerWrap}>
        <span className="material-symbols-outlined" style={{ color: '#005bc0', fontSize: '20px' }} aria-hidden>
          send
        </span>
        <div>
          <div style={headerTitle}>{t('telegram_title')}</div>
          <div style={headerSub}>{t('telegram_subtitle')}</div>
        </div>
      </div>

      <div style={rowOuter}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', color: '#111', fontWeight: 600 }}>
            {status.linked ? t('telegram_connected_as', { username: status.username ?? '' }) : t('telegram_not_connected')}
          </div>
        </div>
        {status.linked ? (
          <button onClick={() => unlinkMutation.mutate()} disabled={unlinkMutation.isPending} style={secondaryBtn}>
            {unlinkMutation.isPending ? '…' : t('telegram_disconnect_button')}
          </button>
        ) : (
          <button onClick={() => linkMutation.mutate()} disabled={linkMutation.isPending} style={primaryBtn}>
            {linkMutation.isPending ? '…' : t('telegram_connect_button')}
          </button>
        )}
      </div>

      {unlinkMutation.isError && (
        <div style={{ fontSize: '12px', color: '#dc2626', paddingTop: '8px' }}>{t('telegram_unlink_failed')}</div>
      )}

      {linkMutation.isError && (
        <div style={{ fontSize: '12px', color: '#dc2626', paddingTop: '8px' }}>{t('telegram_link_failed')}</div>
      )}

      {linkMutation.data && (
        <div style={{ borderTop: '1px solid rgba(43,52,55,0.06)', marginTop: '8px', paddingTop: '12px' }}>
          {linkMutation.data.deepLink ? (
            <a href={linkMutation.data.deepLink} target="_blank" rel="noreferrer" style={primaryBtnFull}>
              {t('telegram_open_bot')}
            </a>
          ) : null}
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
            {t('telegram_manual_code', { code: linkMutation.data.code })}
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
            {t('telegram_code_expires')}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── styles ─────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '14px',
  padding:      '16px 18px',
  boxShadow:    '0 1px 3px rgba(0,0,0,0.05)',
  marginBottom: '24px',
};

const headerWrap: React.CSSProperties = {
  display:      'flex',
  alignItems:   'flex-start',
  gap:          '12px',
  marginBottom: '12px',
};

const headerTitle: React.CSSProperties = {
  fontFamily:   "'Manrope', system-ui, sans-serif",
  fontWeight:   700,
  fontSize:     '15px',
  color:        '#111',
  marginBottom: '2px',
};

const headerSub: React.CSSProperties = {
  fontSize: '12px',
  color:    '#6b7280',
};

const rowOuter: React.CSSProperties = {
  display:       'flex',
  alignItems:    'center',
  gap:           '10px',
  paddingTop:    '4px',
  paddingBottom: '4px',
};

const primaryBtn: React.CSSProperties = {
  background:   '#005bc0',
  color:        '#fff',
  border:       'none',
  borderRadius: '8px',
  padding:      '8px 16px',
  fontSize:     '13px',
  fontWeight:   600,
  cursor:       'pointer',
};

const primaryBtnFull: React.CSSProperties = {
  ...primaryBtn,
  display:        'block',
  textAlign:      'center',
  textDecoration: 'none',
};

const secondaryBtn: React.CSSProperties = {
  background:   'transparent',
  color:        '#dc2626',
  border:       '1px solid #fca5a5',
  borderRadius: '8px',
  padding:      '8px 16px',
  fontSize:     '13px',
  fontWeight:   600,
  cursor:       'pointer',
};
